import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import pressLogos from '@/static/assets/pet/press_logos.svg';
import pressLogosSm from '@/static/assets/pet/press_logos_mobile.svg';
import { PRODUCT_PET } from '@/utils/products';
import { Box, Flex, ResponsiveBreak, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import Image from 'next/image';
import type React from 'react';
import styled from 'styled-components';
import { PetLandingPageType } from './shared';
import usePetBouncingButtonAnimation from './usePetBouncingButtonAnimation';

const DoGoodWrapper = styled(Section)`
  width: 100%;
`;

const Heading = styled(SectionTitle)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 16px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 48px;
    `}
`;

const MainCTA = styled(JoinCTA)<{ played: boolean }>`
  width: 228px;
  z-index: 1;
  transform: rotate(${({ played }) => (played ? '8deg' : '0')})
    translate(5px, ${({ played }) => (played ? '33px' : '64px')});
  -webkit-tap-highlight-color: transparent;
  color: transparent;
  background: transparent !important;
  box-shadow: none;
  user-select: none;
`;

const ButtonLottie = styled(Box)`
  width: 625px;
  height: 250px;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -55%);
  z-index: 1;
`;

const PetAnimationWrapper = styled(Box)`
  width: 100%;
  height: 294px;
  position: relative;
  top: 20px;
  background: linear-gradient(
    ${themedColor('white')},
    ${themedColor('white')} 84%,
    ${themedColor('cloud')} 84%,
    ${themedColor('cloud')}
  );

  ${MEDIA_QUERIES.tabletAndDesktop`
        height: 487px;

        path {
            stroke-width: 2 !important;
            vector-effect: non-scaling-stroke;
        }
    `}

  svg {
    path {
      stroke-width: 1.5;
      vector-effect: non-scaling-stroke;
    }
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    svg g:nth-child(7) {
      /* dogs eyes in IE */
      mask: none;
    }
  }
`;

const PressLogosWrapper = styled(Flex).attrs({ justifyContent: 'center' })`
  background-color: ${themedColor('cloud')};
  width: 100%;
  margin-top: 20px;
  padding-bottom: 80px;

  ${MEDIA_QUERIES.mobile`
        padding-bottom: 50px;
    `}
`;

const Img = styled(Image)`
  max-width: 100%;
  object-fit: cover;
`;

const PressLogoDesktopImg = styled(Image)`
  display: none;

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: block;
        width: 884px;
    `}
`;

const PressLogoMobileImg = styled(Img)`
  display: block;
  width: 292px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        display: none;
    `}
`;

const AnimationWrapper = styled(Box)`
  width: 100%;
  position: relative;
  isolation: isolate;
`;

const PRESS_LOGOS_ALT_TEXT = 'assorted publications where Lemonade is covered';

interface DoGoodProps {
  petLandingPageType: PetLandingPageType;
}
const DoGood: React.FC<DoGoodProps> = ({ petLandingPageType }) => {
  const { isPlayed, petWrapperNodeRef, buttonWrapperNodeRef, ref } = usePetBouncingButtonAnimation(petLandingPageType);

  return (
    <DoGoodWrapper justifyContent="center" pt={[40, 70]}>
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>
          Good for Your&nbsp;
          {petLandingPageType === PetLandingPageType.PetInsurance
            ? 'Pet'
            : petLandingPageType[0].toUpperCase() + petLandingPageType.slice(1)}
          ,
          <ResponsiveBreak display={['block', 'block', 'inline']} />
          and for The World
        </Heading>
        <SubHeading>
          We’re a public benefit corporation. After covering claims and costs,
          <ResponsiveBreak display={['inline', 'block', 'block']} />
          Lemonade donates to a social cause you choose — including animal welfare organizations.
        </SubHeading>
      </Flex>
      <MainCTA
        played={isPlayed}
        flavor="primary"
        glowing
        data-source="pet"
        product={PRODUCT_PET}
        analyticsParams={{
          position: 'bottom',
          pet_lp_type: petLandingPageType,
        }}
      >
        Check our prices
      </MainCTA>
      <AnimationWrapper ref={ref}>
        <ButtonLottie ref={buttonWrapperNodeRef} />
        <PetAnimationWrapper ref={petWrapperNodeRef} alignItems="center" justifyContent="center" />
      </AnimationWrapper>
      <PressLogosWrapper px={[24, 24, 0]}>
        <PressLogoDesktopImg alt={PRESS_LOGOS_ALT_TEXT} src={pressLogos} />
        <PressLogoMobileImg alt={PRESS_LOGOS_ALT_TEXT} src={pressLogosSm} />
      </PressLogosWrapper>
    </DoGoodWrapper>
  );
};

export default DoGood;
